/*
 * Public landing page for NDM Advisors — served at "/" without any auth.
 * Purely presentational (mock content). All authenticated routes remain
 * unchanged; the navbar Login button links to the existing /login route.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Search,
  Bell,
  UserCircle2,
  Menu,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calculator,
  Percent,
  FileSpreadsheet,
  Landmark,
  Mail,
  MapPin,
  Phone,
  AlertOctagon,
  Clock,
  Info,
  ExternalLink,
  Home as HomeIcon,
  BookOpen,
  CheckCircle2,
  FileText,
  Link2,
  ScrollText,
  ShieldCheck,
  CalendarClock,
  X as XIcon,
  Newspaper,
  Gavel,
  ArrowRight,
} from "lucide-react";
import {
  CATEGORIES as CONTENT_CATEGORIES,
  CONTENT_PAGES,
  LAST_UPDATED,
  pageKey,
  getPage,
} from "@/data/complianceContent";

// ---------- CMS → landing mappers ----------
const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function circularToStory(c) {
  const f = c.fields || {};
  const excerptSource = f.plainSummary || f.subject || f.fullText || "";
  const dt = f.issueDate || c.publishedAt || c.createdAt;
  return {
    tag: f.authority || f.docType || "Update",
    title: c.title,
    excerpt: stripHtml(excerptSource).slice(0, 220),
    date: dt ? new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "",
    img: null,
    // No public detail page (yet) — keep as internal card, no url.
  };
}

const CMS_URGENCY_TO_UI = { high: "critical", medium: "upcoming", low: "info" };
function cmsAlertToUi(a) {
  const f = a.fields || {};
  const dt = f.effectiveDate || a.publishedAt || a.createdAt;
  return {
    urgency: CMS_URGENCY_TO_UI[(f.urgencyLevel || "low").toLowerCase()] || "info",
    title: f.headline || a.title,
    desc: stripHtml(f.body || "").slice(0, 260) || (f.actions || []).filter(Boolean).slice(0, 2).join(" · "),
    date: dt ? new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "",
  };
}


// ---------- Design tokens ----------
const C = {
  bg: "#ffffff",
  navy: "#1a1a2e",
  brand: "#6b6bb5",
  indigo: "#2d2d7a",
  gray: "#f5f5f8",
  border: "#e0e0e8",
  body: "#2c2c3e",
  muted: "#888888",
  red: "#c0392b",
  amber: "#e67e22",
};

const LOGO_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/wingman/d52fcc99-6323-4175-a4bb-945511635e75/attachments/948677adb4084671bd935741a72dd65c_NDM%20LOGO.jpeg";

// ---------- Data (mock) ----------
// Sidebar drives content selection; the real categories + sub-items come
// from the static compliance knowledge base (`data/complianceContent.js`).
const CATEGORIES = CONTENT_CATEGORIES;

const STORIES = [
  {
    tag: "Income Tax",
    title: "CBDT extends tax audit report filing deadline for FY 2025-26",
    excerpt:
      "The Central Board of Direct Taxes has notified a one-month extension for taxpayers required to file Form 3CA/3CB and 3CD reports.",
    date: "12 Feb 2026",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=400&q=60",
  },
  {
    tag: "GST",
    title: "GST Council approves rate rationalisation on select services",
    excerpt:
      "The 54th Council meeting cleared changes to GST rates for online gaming and job-work services, effective from 1 April 2026.",
    date: "09 Feb 2026",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=400&q=60",
  },
  {
    tag: "Company Law",
    title: "MCA rolls out V3 e-filing portal enhancements for FY 26",
    excerpt:
      "New XBRL validation checks and offline filing utilities for AOC-4 and MGT-7 are now live on the V3 portal.",
    date: "06 Feb 2026",
    img: "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=400&q=60",
  },
  {
    tag: "SEBI",
    title: "SEBI mandates ESG disclosures in BRSR Core from FY 26-27",
    excerpt:
      "Top 250 listed entities must obtain reasonable assurance on nine BRSR Core parameters, phased in over two years.",
    date: "02 Feb 2026",
    img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=400&q=60",
  },
  {
    tag: "FEMA",
    title: "RBI liberalises overseas investment reporting timelines",
    excerpt:
      "Delayed reporting under the ODI framework can now be regularised with lower compounding fees for first-time defaulters.",
    date: "28 Jan 2026",
    img: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=400&q=60",
  },
  {
    tag: "Labour",
    title: "Central Government notifies four Labour Codes with April roll-out",
    excerpt:
      "The Wage, Industrial Relations, Social Security and OSH codes will supersede 29 existing statutes across establishments.",
    date: "22 Jan 2026",
    img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=400&q=60",
  },
];

const ALERTS = [
  {
    urgency: "critical",
    title: "TDS Return Q3 FY 25-26 due in 3 days",
    desc: "Form 26Q / 27Q filings for the December quarter must be uploaded on the TIN-NSDL portal.",
    date: "15 Feb 2026",
  },
  {
    urgency: "upcoming",
    title: "GSTR-9 Annual Return — FY 24-25",
    desc: "Turnover above ₹2 crore requires filing along with GSTR-9C reconciliation.",
    date: "31 Mar 2026",
  },
  {
    urgency: "critical",
    title: "Advance Tax — 4th installment",
    desc: "Corporate and non-corporate assessees must pay by 15 March to avoid 234C interest.",
    date: "15 Mar 2026",
  },
  {
    urgency: "info",
    title: "New draft rules for angel-tax valuation released",
    desc: "CBDT invites public comments until 28 Feb; likely impact on start-up funding rounds.",
    date: "08 Feb 2026",
  },
];

const TOOLS = [
  { icon: Percent, label: "GST Calculator" },
  { icon: Calculator, label: "TDS Rate Finder" },
  { icon: FileSpreadsheet, label: "Compliance Calendar" },
  { icon: Landmark, label: "HSN / SAC Lookup" },
];

// ==================================================================

export default function LandingPage() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // null = show home (stories/alerts/contact); string "cat:sub" = show content page
  const [activeItem, setActiveItem] = useState(null);
  const contentTopRef = useRef(null);

  const activePage = useMemo(() => {
    if (!activeItem) return null;
    return CONTENT_PAGES[activeItem] || null;
  }, [activeItem]);

  // When user picks a sub-item, scroll main column back to top for a
  // predictable reading experience.
  useEffect(() => {
    if (activeItem && contentTopRef.current) {
      contentTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeItem]);

  const handleSelect = (key) => {
    setActiveItem(key);
    setMobileNavOpen(false);
  };

  // Live news feed (NewsAPI, cached server-side). Falls back to curated arrays
  // below when the fetch is loading or the backend returns `fallback: true`.
  const newsQ = useQuery({
    queryKey: ["public-news"],
    queryFn: api.publicNews,
    staleTime: 15 * 60 * 1000, // 15 min — server also caches
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: !activeItem, // only fetch news for the home view
  });
  // Admin-published CMS content bundled for the landing page.
  const cmsQ = useQuery({
    queryKey: ["public-landing-cms"],
    queryFn: api.publicLandingCms,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: !activeItem,
  });
  const feed = newsQ.data;
  const useFeed = feed && !feed.fallback && Array.isArray(feed.stories);
  const cmsData = cmsQ.data || {};
  const cmsCirculars = cmsData.circulars || [];
  const cmsAlerts = cmsData.alerts || [];
  const cmsHighAlerts = cmsData.highAlerts || [];
  const cmsFeaturedBlogs = cmsData.featuredBlogs || [];

  // Merge CMS circulars (newest first) with the news feed. CMS wins on order.
  const baseStories = useFeed && feed.stories.length ? feed.stories : STORIES;
  const storiesData = [
    ...cmsCirculars.map(circularToStory),
    ...baseStories,
  ];
  // Merge CMS alerts with the news alerts (CMS first for admin control).
  const baseAlerts = useFeed && feed.alerts.length ? feed.alerts : ALERTS;
  const alertsData = [
    ...cmsAlerts.map(cmsAlertToUi),
    ...baseAlerts,
  ];
  const stale = !!(feed && feed.stale && !feed.fallback);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBanner =
    !activeItem && !bannerDismissed && cmsHighAlerts.length > 0;

  return (
    <div
      className="min-h-screen"
      data-testid="landing-page"
      style={{
        background: C.bg,
        color: C.body,
        fontFamily:
          "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        fontSize: 14,
      }}
    >
      <TopNav
        onHamburgerClick={() => setMobileNavOpen(true)}
        onDesktopToggle={() => setSidebarOpen((s) => !s)}
        onHome={() => setActiveItem(null)}
      />

      {/* High-urgency dismissible banner (top of landing page) */}
      {showBanner && (
        <HighAlertBanner
          alerts={cmsHighAlerts}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Body layout — sidebar | main | right rail */}
      <div className={showBanner ? "pt-[76px]" : "pt-[76px]"}>
        <div className="mx-auto max-w-[1440px] w-full flex gap-0 lg:gap-6 px-3 md:px-6">
          {/* Sidebar (desktop) */}
          <aside
            className={`hidden lg:block shrink-0 transition-[width] duration-200`}
            style={{ width: sidebarOpen ? 260 : 56 }}
          >
            <Sidebar
              collapsed={!sidebarOpen}
              onToggle={() => setSidebarOpen((s) => !s)}
              activeItem={activeItem}
              onSelect={handleSelect}
            />
          </aside>

          {/* Main + right rail */}
          <main className="flex-1 min-w-0 py-6 md:py-8 flex flex-col lg:flex-row gap-6">
            <section className="flex-1 min-w-0 flex flex-col gap-10">
              <span ref={contentTopRef} />
              {activePage ? (
                <ContentRenderer page={activePage} pageId={activeItem} />
              ) : (
                <>
                  <LatestStories
                    stories={storiesData}
                    loading={newsQ.isLoading}
                    stale={stale}
                  />
                  <Alerts alerts={alertsData} />
                  {cmsFeaturedBlogs.length > 0 && (
                    <FeaturedBlogsRow blogs={cmsFeaturedBlogs} />
                  )}
                  <ContactUs />
                </>
              )}
            </section>

            {/* Right rail */}
            <aside className="w-full lg:w-[280px] shrink-0 flex flex-col gap-5">
              <ToolsPanel />
              <NewsletterPanel />
            </aside>
          </main>
        </div>
        <Footer />
      </div>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          onClick={() => setMobileNavOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute left-0 top-0 h-full w-72 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="h-[76px] flex items-center justify-between px-4"
              style={{ borderBottom: `2px solid ${C.brand}` }}
            >
              <img
                src={LOGO_URL}
                alt="NDM Advisors"
                style={{ height: 40 }}
                className="object-contain"
              />
              <button
                onClick={() => setMobileNavOpen(false)}
                className="h-9 w-9 rounded-md hover:bg-gray-100 inline-flex items-center justify-center"
                aria-label="Close menu"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
            <div className="p-3">
              <Sidebar
                collapsed={false}
                onToggle={() => {}}
                noToggle
                mobile
                activeItem={activeItem}
                onSelect={handleSelect}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Content Renderer — statutory knowledge base page
// ==================================================================
// Map static compliance-content category keys → CMS category names.
const CAT_KEY_TO_CMS = {
  "income-tax": "Income Tax",
  "gst": "GST",
  "company-sebi": "Company and SEBI Laws",
  "fema-fcra": "FEMA and FCRA",
  "international-tax": "International Tax",
  "transfer-pricing": "Transfer Pricing",
  "labour-laws": "Labour Laws",
};

function ContentRenderer({ page, pageId }) {
  const { hero, summary, tables = [], checklist = [], forms = [], portals = [] } =
    page;
  const authority = page.authority || summary?.authority || "";
  const [cat, sub] = (pageId || "").split(":");
  const categoryLabel =
    CATEGORIES.find((c) => c.key === cat)?.label || "Knowledge Base";
  const cmsCategory = CAT_KEY_TO_CMS[cat];

  // Fetch CMS content scoped to this category.
  const caseLawsQ = useQuery({
    queryKey: ["public-cms", "case-laws", cmsCategory],
    queryFn: () => api.publicCaseLaws({ category: cmsCategory }),
    enabled: !!cmsCategory,
    staleTime: 60_000,
  });
  const actsQ = useQuery({
    queryKey: ["public-cms", "acts", cmsCategory],
    queryFn: () => api.publicActs({ category: cmsCategory }),
    enabled: !!cmsCategory,
    staleTime: 60_000,
  });
  const circularsQ = useQuery({
    queryKey: ["public-cms", "circulars", cmsCategory],
    queryFn: () => api.publicCirculars({ category: cmsCategory }),
    enabled: !!cmsCategory,
    staleTime: 60_000,
  });
  const caseLaws = caseLawsQ.data?.items || [];
  const acts = actsQ.data?.items || [];
  const circulars = circularsQ.data?.items || [];

  return (
    <article data-testid="landing-content-page" className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <nav
        className="text-[12px] flex items-center gap-1.5"
        style={{ color: C.muted }}
        aria-label="Breadcrumb"
      >
        <span>Knowledge Base</span>
        <ChevronRight className="h-3 w-3" />
        <span style={{ color: C.brand }}>{categoryLabel}</span>
        <ChevronRight className="h-3 w-3" />
        <span style={{ color: C.navy, fontWeight: 500 }}>{sub}</span>
      </nav>

      {/* Hero strip */}
      <header
        className="p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
        style={{
          background: `linear-gradient(135deg, ${C.navy} 0%, ${C.indigo} 100%)`,
          borderRadius: 10,
          color: "#fff",
        }}
      >
        <div className="flex-1 min-w-0">
          <div
            className="text-[11px] font-medium uppercase tracking-[0.12em] mb-2"
            style={{ color: "rgba(255,255,255,0.72)" }}
          >
            {categoryLabel}
          </div>
          <h1
            className="leading-tight"
            style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {hero.title}
          </h1>
          {hero.description && (
            <p
              className="mt-2 leading-snug max-w-3xl"
              style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
            >
              {hero.description}
            </p>
          )}
        </div>
        {authority && (
          <span
            data-testid="landing-content-authority"
            className="inline-flex items-center gap-1.5 shrink-0 self-start md:self-center"
            style={{
              background: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.28)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              padding: "8px 14px",
              borderRadius: 999,
            }}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {authority}
          </span>
        )}
      </header>

      {/* Two column body */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left summary panel */}
        <aside className="lg:col-span-5 flex flex-col gap-4">
          <div
            className="p-5"
            style={{
              background: C.gray,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
            }}
          >
            <h3
              className="mb-4 inline-flex items-center gap-2"
              style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
            >
              <BookOpen className="h-4 w-4" style={{ color: C.brand }} />
              At a Glance
            </h3>
            <dl className="flex flex-col gap-3">
              <MetaRow
                icon={ScrollText}
                label="Governing"
                value={summary.governing}
              />
              <MetaRow
                icon={Landmark}
                label="Authority"
                value={summary.authority}
              />
              <MetaRow
                icon={CalendarClock}
                label="Effective"
                value={summary.effective}
              />
            </dl>
          </div>

          {summary.overview && (
            <div
              className="p-5"
              style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
              }}
            >
              <h3
                className="mb-2"
                style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
              >
                Overview
              </h3>
              <p
                className="leading-relaxed"
                style={{ fontSize: 13.5, color: C.body }}
              >
                {summary.overview}
              </p>
            </div>
          )}
        </aside>

        {/* Right — checklist + tables */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          {checklist.length > 0 && (
            <div
              className="p-5"
              style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderLeft: `4px solid ${C.brand}`,
                borderRadius: 10,
              }}
            >
              <h3
                className="mb-3 inline-flex items-center gap-2"
                style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
              >
                <CheckCircle2 className="h-4 w-4" style={{ color: C.brand }} />
                Compliance Checklist
              </h3>
              <ul className="flex flex-col gap-2">
                {checklist.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 leading-snug"
                    style={{ fontSize: 13.5, color: C.body }}
                  >
                    <span
                      className="h-5 w-5 shrink-0 rounded-full inline-flex items-center justify-center text-[11px]"
                      style={{
                        background: `${C.brand}18`,
                        color: C.indigo,
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tables.map((tbl, i) => (
            <ContentTable key={i} table={tbl} />
          ))}
        </div>
      </div>

      {/* Forms & Portals row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {forms.length > 0 && (
          <div
            className="p-5"
            style={{
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
            }}
          >
            <h3
              className="mb-3 inline-flex items-center gap-2"
              style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
            >
              <FileText className="h-4 w-4" style={{ color: C.brand }} />
              Important Forms
            </h3>
            <ul className="flex flex-col gap-1.5">
              {forms.map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 leading-snug"
                  style={{ fontSize: 13, color: C.body }}
                >
                  <span
                    className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ background: C.brand }}
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {portals.length > 0 && (
          <div
            className="p-5"
            style={{
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
            }}
          >
            <h3
              className="mb-3 inline-flex items-center gap-2"
              style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
            >
              <Link2 className="h-4 w-4" style={{ color: C.brand }} />
              Key Portals & Contacts
            </h3>
            <ul className="flex flex-col gap-2">
              {portals.map((p, i) => (
                <li key={i}>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 hover:underline"
                    style={{ fontSize: 13, color: C.indigo, fontWeight: 500 }}
                  >
                    {p.label}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* CMS-published content sections (case laws / acts / circulars) */}
      {(caseLaws.length > 0 || acts.length > 0 || circulars.length > 0) && (
        <div
          className="flex flex-col gap-6 pt-4 mt-2"
          style={{ borderTop: `1px solid ${C.border}` }}
          data-testid="landing-content-cms-sections"
        >
          {circulars.length > 0 && (
            <CmsSection
              title="Latest Circulars & Notifications"
              icon={Newspaper}
              testId="landing-cms-circulars"
              items={circulars}
            />
          )}
          {caseLaws.length > 0 && (
            <CmsSection
              title="Case Laws"
              icon={Gavel}
              testId="landing-cms-case-laws"
              items={caseLaws}
            />
          )}
          {acts.length > 0 && (
            <CmsSection
              title="Acts and Statutes"
              icon={BookOpen}
              testId="landing-cms-acts"
              items={acts}
            />
          )}
        </div>
      )}

      {/* Footer meta */}
      <div
        className="flex items-center justify-between flex-wrap gap-2 pt-2"
        style={{
          borderTop: `1px solid ${C.border}`,
          color: C.muted,
          fontSize: 12,
        }}
      >
        <span>
          Last Updated: <strong style={{ color: C.body }}>{LAST_UPDATED}</strong>
        </span>
        <span>
          Content is illustrative — verify with official authority notifications.
        </span>
      </div>
    </article>
  );
}

function CmsSection({ title, icon: Icon, items, testId }) {
  return (
    <section data-testid={testId}>
      <h3
        className="mb-3 inline-flex items-center gap-2"
        style={{ fontSize: 16, fontWeight: 700, color: C.navy }}
      >
        <Icon className="h-4 w-4" style={{ color: C.brand }} />
        {title}
        <span
          className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: "#dcfce7", color: "#166534" }}
        >
          Live
        </span>
      </h3>
      <div className="flex flex-col gap-3">
        {items.map((it) => (
          <CmsSectionCard key={it.id} content={it} />
        ))}
      </div>
    </section>
  );
}

// Local lightweight renderer to avoid importing the admin PublicContentCard
// bundle into the landing page (keeps this route lean).
function CmsSectionCard({ content }) {
  const t = content.contentType;
  const f = content.fields || {};
  const strip = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return (
    <a
      href={t === "blog" ? `/blog/${content.slug}` : undefined}
      className="block p-4 rounded-lg no-underline"
      style={{
        background: "#fff",
        border: `1px solid ${C.border}`,
        color: "inherit",
        cursor: t === "blog" ? "pointer" : "default",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(107,107,181,0.15)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        {t === "case_law" && f.court && (
          <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: C.indigo, color: "#fff" }}>
            {f.court}
          </span>
        )}
        {t === "circular" && f.docType && (
          <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: C.indigo, color: "#fff" }}>
            {f.docType}
          </span>
        )}
        {t === "act" && f.authority && (
          <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: C.indigo, color: "#fff" }}>
            {f.authority}
          </span>
        )}
        {f.citation && (
          <span className="text-[11px]" style={{ color: C.brand }}>{f.citation}</span>
        )}
        {f.refNumber && (
          <span className="text-[11px]" style={{ color: C.brand }}>{f.refNumber}</span>
        )}
      </div>
      <div className="text-[14px] font-bold leading-snug" style={{ color: C.navy }}>
        {content.title}
      </div>
      {(f.keyPrinciple || f.plainSummary || f.description || f.subject) && (
        <div className="mt-1 text-[12.5px] leading-snug" style={{ color: C.body }}>
          {(f.keyPrinciple || strip(f.plainSummary || f.description || f.subject) || "").slice(0, 220)}
        </div>
      )}
    </a>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5">
      <Icon
        className="h-4 w-4 mt-0.5 shrink-0"
        style={{ color: C.brand }}
      />
      <div className="min-w-0">
        <div
          className="text-[11px] font-medium uppercase tracking-wider"
          style={{ color: C.muted }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13.5, color: C.navy, fontWeight: 500 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function ContentTable({ table }) {
  const { title, columns = [], rows = [] } = table;
  return (
    <div
      className="overflow-hidden"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
      }}
    >
      {title && (
        <div
          className="px-4 py-3"
          style={{
            borderBottom: `1px solid ${C.border}`,
            fontSize: 14,
            fontWeight: 700,
            color: C.navy,
          }}
        >
          {title}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.gray }}>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className="text-left px-4 py-2.5"
                  style={{
                    color: C.navy,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: "0.02em",
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                style={{
                  borderBottom:
                    i === rows.length - 1 ? "none" : `1px solid ${C.border}`,
                }}
              >
                {Array.isArray(r)
                  ? r.map((cell, j) => (
                      <td
                        key={j}
                        className="px-4 py-2.5 align-top leading-snug"
                        style={{ color: C.body }}
                      >
                        {cell}
                      </td>
                    ))
                  : (
                    // Fallback in case a row is a string (single-col table)
                    <td
                      className="px-4 py-2.5 align-top leading-snug"
                      style={{ color: C.body }}
                    >
                      {r}
                    </td>
                  )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ==================================================================
// TopNav
// ==================================================================
function TopNav({ onHamburgerClick, onDesktopToggle, onHome }) {
  return (
    <header
      data-testid="landing-navbar"
      className="fixed top-0 inset-x-0 z-30"
      style={{
        background: C.bg,
        borderBottom: `2px solid ${C.brand}`,
        boxShadow: "0 4px 12px rgba(107,107,181,0.10)",
      }}
    >
      <div className="mx-auto max-w-[1440px] h-[76px] px-3 md:px-6 flex items-center gap-3">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={onHamburgerClick}
          className="lg:hidden h-10 w-10 rounded-md inline-flex items-center justify-center hover:bg-gray-100"
          aria-label="Open menu"
          data-testid="landing-mobile-menu"
        >
          <Menu className="h-5 w-5" style={{ color: C.brand }} />
        </button>

        {/* Logo + tagline — clicking returns to home view */}
        <button
          type="button"
          onClick={onHome}
          data-testid="landing-home-btn"
          className="flex items-center gap-3 shrink-0 hover:opacity-90 transition-opacity"
          aria-label="Go to home"
        >
          <img
            src={LOGO_URL}
            alt="NDM Advisors"
            style={{ height: 48 }}
            className="object-contain select-none"
            draggable={false}
          />
          <div
            className="hidden sm:block leading-tight text-left"
            style={{ color: C.brand }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: C.navy,
                letterSpacing: "0.01em",
              }}
            >
              NDM Advisors
            </div>
            <div className="text-[12px]" style={{ color: C.brand }}>
              Compliance. Intelligence. Advisory.
            </div>
          </div>
        </button>

        {/* Search */}
        <div className="flex-1 max-w-2xl mx-4 hidden md:block">
          <div
            className="relative flex items-center"
            style={{
              background: C.gray,
              borderRadius: 999,
              height: 40,
              padding: "0 14px 0 40px",
            }}
          >
            <Search
              className="absolute left-3 h-4 w-4"
              style={{ color: C.brand }}
            />
            <input
              type="search"
              placeholder="Search acts, rules, and compliance updates"
              className="w-full h-full bg-transparent outline-none text-[14px]"
              style={{ color: C.body }}
              data-testid="landing-search"
            />
          </div>
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-2 md:gap-3 ml-auto">
          <Link
            to="/login"
            data-testid="landing-login-btn"
            className="h-10 px-4 rounded-md text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
            style={{ background: C.navy, color: "#fff" }}
          >
            Login
          </Link>
          <button
            type="button"
            className="relative h-10 w-10 rounded-md hover:bg-gray-100 inline-flex items-center justify-center"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" style={{ color: C.brand }} />
            <span
              className="absolute top-1.5 right-2 h-2 w-2 rounded-full"
              style={{ background: C.red }}
            />
          </button>
          <button
            type="button"
            className="h-10 w-10 rounded-md hover:bg-gray-100 inline-flex items-center justify-center"
            aria-label="Account"
          >
            <UserCircle2 className="h-6 w-6" style={{ color: C.brand }} />
          </button>
        </div>
      </div>
    </header>
  );
}

// ==================================================================
// Sidebar (collapsible)
// ==================================================================
function Sidebar({ collapsed, onToggle, noToggle, mobile, activeItem, onSelect }) {
  // Auto-open the category that contains the active item; otherwise default
  // to the first category so users can immediately see the knowledge base.
  const activeCat = activeItem ? activeItem.split(":")[0] : null;
  const [openKey, setOpenKey] = useState(activeCat || CATEGORIES[0].key);

  useEffect(() => {
    if (activeCat) setOpenKey(activeCat);
  }, [activeCat]);

  return (
    <div
      className="sticky top-[92px] rounded-lg"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        maxHeight: "calc(100vh - 108px)",
        overflowY: "auto",
      }}
      data-testid={mobile ? "landing-sidebar-mobile" : "landing-sidebar"}
    >
      {/* Header + collapse toggle */}
      <div
        className="flex items-center justify-between px-3 py-3"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        {!collapsed && (
          <div
            className="text-[11px] font-medium uppercase tracking-wider"
            style={{ color: C.brand }}
          >
            Categories
          </div>
        )}
        {!noToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="h-7 w-7 rounded-md inline-flex items-center justify-center hover:bg-gray-100"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="landing-sidebar-toggle"
            style={{ color: C.brand }}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Home entry */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors"
        style={{
          color: activeItem === null ? "#fff" : C.navy,
          background: activeItem === null ? C.indigo : "transparent",
          fontWeight: 500,
          borderBottom: `1px solid ${C.border}`,
        }}
        data-testid="landing-cat-home"
        title={collapsed ? "Home" : undefined}
      >
        <span
          className="h-7 w-7 rounded-md inline-flex items-center justify-center shrink-0"
          style={{
            background:
              activeItem === null ? "rgba(255,255,255,0.15)" : C.gray,
            color: activeItem === null ? "#fff" : C.brand,
          }}
        >
          <HomeIcon className="h-4 w-4" />
        </span>
        {!collapsed && (
          <span className="text-[14px] flex-1 truncate">Home</span>
        )}
      </button>

      {/* Categories */}
      <nav className="py-2">
        {CATEGORIES.map((cat) => {
          const isOpen = openKey === cat.key;
          return (
            <div key={cat.key}>
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? "" : cat.key)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors"
                style={{
                  color: isOpen ? "#fff" : C.navy,
                  background: isOpen ? C.brand : "transparent",
                  fontWeight: 500,
                }}
                data-testid={`landing-cat-${cat.key}`}
                title={collapsed ? cat.label : undefined}
              >
                <span
                  className="h-7 w-7 rounded-md inline-flex items-center justify-center shrink-0"
                  style={{
                    background: isOpen ? "rgba(255,255,255,0.15)" : C.gray,
                    color: isOpen ? "#fff" : C.brand,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {cat.label
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)}
                </span>
                {!collapsed && (
                  <>
                    <span className="text-[14px] flex-1 truncate">
                      {cat.label}
                    </span>
                    {isOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </>
                )}
              </button>
              {isOpen && !collapsed && (
                <ul className="py-1 pl-3">
                  {cat.items.map((item) => {
                    const key = pageKey(cat.key, item);
                    const active = activeItem === key;
                    return (
                      <li key={item}>
                        <button
                          type="button"
                          onClick={() => onSelect(key)}
                          data-testid={`landing-sub-${cat.key}-${item
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                          className="w-full text-left px-3 py-2 text-[13px] transition-colors rounded-md"
                          style={{
                            color: active ? "#fff" : C.body,
                            background: active ? C.brand : "transparent",
                            borderLeft: active
                              ? `3px solid ${C.indigo}`
                              : "3px solid transparent",
                          }}
                          onMouseEnter={(e) => {
                            if (active) return;
                            e.currentTarget.style.background = C.gray;
                          }}
                          onMouseLeave={(e) => {
                            if (active) return;
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {item}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

// ==================================================================
// Section 1: Latest Stories
// ==================================================================
function LatestStories({ stories, loading, stale }) {
  return (
    <section data-testid="landing-stories">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1
            style={{ color: C.navy, fontSize: 22, fontWeight: 700 }}
            className="tracking-tight flex items-center gap-2"
          >
            Latest Stories
            {!loading && !stale && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: "#dcfce7", color: "#166534" }}
                title="Live from NewsAPI"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: "#22c55e" }}
                />
                Live
              </span>
            )}
            {stale && (
              <span
                className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: C.gray, color: C.muted }}
                title="Showing cached news"
              >
                Cached
              </span>
            )}
          </h1>
          <p className="text-[13px]" style={{ color: C.muted }}>
            {loading
              ? "Loading the latest compliance updates…"
              : "Curated compliance news across statutes."}
          </p>
        </div>
        <a
          href="#archives"
          className="text-[13px] font-medium inline-flex items-center gap-1 hover:underline"
          style={{ color: C.brand }}
        >
          Archives <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <StoryCardSkeleton key={i} />
            ))
          : stories.map((s, i) => (
              <StoryCard key={(s.url || s.title) + i} story={s} />
            ))}
      </div>
    </section>
  );
}

function StoryCardSkeleton() {
  return (
    <div
      className="flex gap-3 p-3 animate-pulse"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
      }}
    >
      <div className="h-24 w-28 rounded-md shrink-0" style={{ background: C.gray }} />
      <div className="flex-1 flex flex-col gap-2">
        <div className="h-4 w-24 rounded" style={{ background: C.gray }} />
        <div className="h-4 w-full rounded" style={{ background: C.gray }} />
        <div className="h-3 w-3/4 rounded" style={{ background: C.gray }} />
      </div>
    </div>
  );
}

function StoryCard({ story }) {
  const Wrapper = story.url ? "a" : "div";
  const wrapperProps = story.url
    ? { href: story.url, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="flex gap-3 p-3 transition-shadow cursor-pointer no-underline"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        color: "inherit",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(107,107,181,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {story.img ? (
        <img
          src={story.img}
          alt=""
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
          className="h-24 w-28 object-cover rounded-md shrink-0"
          style={{ background: C.gray }}
        />
      ) : (
        <div
          className="h-24 w-28 rounded-md shrink-0 flex items-center justify-center text-[11px] uppercase tracking-wider"
          style={{ background: C.gray, color: C.brand, fontWeight: 600 }}
        >
          NDM
        </div>
      )}
      <div className="min-w-0 flex flex-col">
        <div className="flex items-center gap-2 mb-1">
          <span
            style={{
              background: C.brand,
              color: "#fff",
              fontSize: 11,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              padding: "2px 8px",
              borderRadius: 4,
            }}
          >
            {story.tag}
          </span>
          {story.date && (
            <span className="text-[12px]" style={{ color: C.muted }}>
              {story.date}
            </span>
          )}
        </div>
        <h3
          className="line-clamp-2 leading-snug mb-1"
          style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
        >
          {story.title}
        </h3>
        <p
          className="line-clamp-2 text-[13px] leading-snug"
          style={{ color: C.body }}
        >
          {story.excerpt}
        </p>
      </div>
    </Wrapper>
  );
}

// ==================================================================
// Section 2: Alerts & Updates
// ==================================================================
function Alerts({ alerts }) {
  return (
    <section data-testid="landing-alerts">
      <h2
        style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}
        className="mb-4"
      >
        Alerts & Updates
      </h2>
      <div className="flex flex-col gap-3">
        {alerts.map((a, i) => (
          <AlertCard key={(a.url || a.title) + i} alert={a} />
        ))}
      </div>
    </section>
  );
}

function AlertCard({ alert }) {
  const meta = useMemo(() => {
    switch (alert.urgency) {
      case "critical":
        return { color: C.red, icon: AlertOctagon, label: "Critical" };
      case "upcoming":
        return { color: C.amber, icon: Clock, label: "Upcoming" };
      default:
        return { color: C.brand, icon: Info, label: "Informational" };
    }
  }, [alert.urgency]);
  const Icon = meta.icon;
  const Wrapper = alert.url ? "a" : "div";
  const wrapperProps = alert.url
    ? { href: alert.url, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="p-4 flex gap-3 transition-shadow no-underline"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderLeft: `4px solid ${meta.color}`,
        borderRadius: 8,
        color: "inherit",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(107,107,181,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        className="h-9 w-9 rounded-md inline-flex items-center justify-center shrink-0"
        style={{
          background: `${meta.color}14`,
          color: meta.color,
        }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: meta.color,
            }}
          >
            {meta.label}
          </span>
          <span className="text-[12px]" style={{ color: C.muted }}>
            · {alert.date}
          </span>
        </div>
        <h4
          style={{ fontSize: 15, fontWeight: 700, color: C.navy }}
          className="leading-snug mb-1"
        >
          {alert.title}
        </h4>
        <p className="text-[13px] leading-snug" style={{ color: C.body }}>
          {alert.desc}
        </p>
      </div>
    </Wrapper>
  );
}

// ==================================================================
// Section 3: Contact Us
// ==================================================================
function ContactUs() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setBusy(true);
    // Purely presentational — no backend endpoint touched.
    setTimeout(() => {
      setBusy(false);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Thanks! We will get back within one business day.");
    }, 700);
  };

  return (
    <section data-testid="landing-contact" id="contact">
      <div className="text-center mb-6">
        <h2 style={{ color: C.navy, fontSize: 22, fontWeight: 700 }}>
          Contact Us
        </h2>
        <p className="text-[13px] mt-1" style={{ color: C.muted }}>
          Speak to an advisor. We reply within one business day.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="mx-auto w-full max-w-2xl p-6 md:p-8 flex flex-col gap-4"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ContactField
            label="Full Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            testId="contact-name"
          />
          <ContactField
            label="Email Address"
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            testId="contact-email"
          />
        </div>
        <ContactField
          label="Subject"
          value={form.subject}
          onChange={(v) => setForm({ ...form, subject: v })}
          testId="contact-subject"
        />
        <ContactField
          label="Message"
          value={form.message}
          onChange={(v) => setForm({ ...form, message: v })}
          testId="contact-message"
          multiline
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="contact-submit"
          className="h-11 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
          style={{ background: C.navy, color: "#fff" }}
        >
          {busy ? "Sending…" : "Submit"}
        </button>
      </form>

      <div
        className="mx-auto max-w-2xl mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]"
        style={{ color: C.brand }}
      >
        <ContactMeta
          icon={Mail}
          text="Communications@ndm.net.in"
          href="mailto:Communications@ndm.net.in"
        />
        <ContactMeta
          icon={Phone}
          text="+91 98732 10394"
          href="tel:+919873210394"
        />
        <ContactMeta
          icon={MapPin}
          text="1305, 13th Floor, Statesman House, Connaught Place, New Delhi 110001"
          href="https://maps.google.com/?q=Statesman+House+Connaught+Place+New+Delhi+110001"
        />
      </div>
    </section>
  );
}

function ContactField({ label, value, onChange, type = "text", multiline, testId }) {
  const baseStyle = {
    background: C.gray,
    border: `1px solid ${C.border}`,
    color: C.body,
    borderRadius: 6,
    fontSize: 14,
  };
  return (
    <label className="flex flex-col gap-1.5">
      <span
        className="text-[12px] font-medium"
        style={{ color: C.navy, letterSpacing: "0.02em" }}
      >
        {label}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          data-testid={testId}
          className="px-3 py-2.5 outline-none focus-visible:outline-none transition-colors"
          style={baseStyle}
          onFocus={(e) => (e.currentTarget.style.borderColor = C.brand)}
          onBlur={(e) => (e.currentTarget.style.borderColor = C.border)}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="h-10 px-3 outline-none focus-visible:outline-none transition-colors"
          style={baseStyle}
          onFocus={(e) => (e.currentTarget.style.borderColor = C.brand)}
          onBlur={(e) => (e.currentTarget.style.borderColor = C.border)}
        />
      )}
    </label>
  );
}

function ContactMeta({ icon: Icon, text, href }) {
  const inner = (
    <span className="inline-flex items-start gap-1.5 leading-snug">
      <Icon className="h-3.5 w-3.5 mt-[3px] shrink-0" />
      <span>{text}</span>
    </span>
  );
  if (!href) {
    return <div className="text-center sm:text-left">{inner}</div>;
  }
  const external = href.startsWith("http");
  return (
    <a
      href={href}
      {...(external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
      className="text-center sm:text-left hover:underline transition-opacity"
      style={{ color: C.brand }}
    >
      {inner}
    </a>
  );
}

// ==================================================================
// Right rail — Tools + Newsletter
// ==================================================================
function ToolsPanel() {
  return (
    <div
      className="p-4"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
      }}
      data-testid="landing-tools"
    >
      <h3
        className="mb-3"
        style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}
      >
        Tools
      </h3>
      <div className="flex flex-col gap-2.5">
        {TOOLS.map((t) => (
          <ToolCard key={t.label} tool={t} />
        ))}
      </div>
    </div>
  );
}

function ToolCard({ tool }) {
  const Icon = tool.icon;
  return (
    <div
      className="flex items-center gap-3 p-3 transition-shadow"
      style={{
        background: C.gray,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(107,107,181,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        className="h-9 w-9 rounded-md inline-flex items-center justify-center shrink-0"
        style={{ background: "#fff", color: C.brand, border: `1px solid ${C.border}` }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="truncate text-[13px]"
          style={{ color: C.navy, fontWeight: 600 }}
        >
          {tool.label}
        </div>
      </div>
      <button
        type="button"
        onClick={() => toast.message(`${tool.label} — coming soon`)}
        className="h-8 px-3 rounded-md text-[12px] font-medium hover:opacity-90 transition-opacity"
        style={{ background: C.indigo, color: "#fff" }}
      >
        Visit
      </button>
    </div>
  );
}

function NewsletterPanel() {
  const [email, setEmail] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setEmail("");
    toast.success("Subscribed. Watch your inbox on Mondays.");
  };
  return (
    <div
      className="p-4"
      style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
      }}
      data-testid="landing-newsletter"
    >
      <h3
        className="mb-1"
        style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}
      >
        Stay Updated
      </h3>
      <p className="text-[12px] mb-3" style={{ color: C.muted }}>
        Weekly digest of compliance updates.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@firm.com"
          data-testid="landing-newsletter-input"
          className="h-10 px-3 outline-none focus-visible:outline-none transition-colors"
          style={{
            background: C.gray,
            border: `1px solid ${C.border}`,
            color: C.body,
            borderRadius: 6,
            fontSize: 14,
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = C.brand)}
          onBlur={(e) => (e.currentTarget.style.borderColor = C.border)}
        />
        <button
          type="submit"
          data-testid="landing-newsletter-submit"
          className="h-10 rounded-md text-[13px] font-medium hover:opacity-90 transition-opacity"
          style={{ background: C.navy, color: "#fff" }}
        >
          Subscribe
        </button>
      </form>
    </div>
  );
}

// ==================================================================
// Footer
// ==================================================================
function Footer() {
  return (
    <footer
      className="mt-10"
      style={{
        background: C.gray,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div className="mx-auto max-w-[1440px] px-3 md:px-6 py-6 flex flex-col md:flex-row items-center gap-3 justify-between text-[12px]">
        <div style={{ color: C.body }}>
          © {new Date().getFullYear()} NDM Advisors. All rights reserved.
        </div>
        <div className="flex items-center gap-4" style={{ color: C.brand }}>
          <a href="#privacy" className="hover:underline">
            Privacy
          </a>
          <a href="#terms" className="hover:underline">
            Terms
          </a>
          <Link to="/login" className="hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </footer>
  );
}


// ==================================================================
// High-urgency banner (fed by CMS-published alerts, urgency=high)
// ==================================================================
function HighAlertBanner({ alerts, onDismiss }) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    if (alerts.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % alerts.length), 6000);
    return () => clearInterval(t);
  }, [alerts.length]);
  const a = alerts[idx];
  if (!a) return null;
  const f = a.fields || {};
  return (
    <div
      className="fixed top-[76px] inset-x-0 z-20 border-b"
      style={{ background: "#7f1d1d", color: "#fff", borderColor: "#450a0a" }}
      data-testid="landing-high-alert-banner"
    >
      <div className="mx-auto max-w-[1440px] px-3 md:px-6 py-2.5 flex items-center gap-3">
        <span
          className="h-7 w-7 rounded-md inline-flex items-center justify-center shrink-0"
          style={{ background: "rgba(255,255,255,0.15)" }}
        >
          <AlertOctagon className="h-3.5 w-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              High Alert
            </span>
            {f.effectiveDate && (
              <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.75)" }}>
                · {new Date(f.effectiveDate).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}
              </span>
            )}
          </div>
          <div className="text-[13.5px] font-semibold leading-snug truncate">
            {f.headline || a.title}
          </div>
        </div>
        {alerts.length > 1 && (
          <div className="hidden sm:flex items-center gap-1 text-[11px]" style={{ color: "rgba(255,255,255,0.75)" }}>
            {idx + 1} / {alerts.length}
          </div>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss high alert"
          data-testid="landing-high-alert-dismiss"
          className="h-8 w-8 rounded-md inline-flex items-center justify-center hover:bg-white/10"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ==================================================================
// Featured Blog cards row (top 3 admin-featured, latest first)
// ==================================================================
function FeaturedBlogsRow({ blogs }) {
  return (
    <section data-testid="landing-featured-blogs">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2
            style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}
            className="inline-flex items-center gap-2"
          >
            Featured Blogs
          </h2>
          <p className="text-[13px]" style={{ color: C.muted }}>
            Practitioner notes from the NDM Advisors team.
          </p>
        </div>
        <Link
          to="/blog"
          className="text-[13px] font-medium inline-flex items-center gap-1 hover:underline"
          style={{ color: C.brand }}
          data-testid="landing-view-all-blogs"
        >
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {blogs.map((b) => (
          <Link
            key={b.id}
            to={`/blog/${b.slug}`}
            className="group flex flex-col gap-2 rounded-lg border overflow-hidden transition-shadow bg-white"
            style={{ borderColor: C.border }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 14px rgba(107,107,181,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
            }}
            data-testid={`landing-featured-blog-${b.slug}`}
          >
            {b.fields?.coverImage ? (
              <img
                src={b.fields.coverImage}
                alt=""
                className="h-36 w-full object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div
                className="h-36 w-full flex items-center justify-center"
                style={{ background: C.gray, color: C.brand }}
              >
                <span className="text-[10px] uppercase tracking-widest font-medium">
                  NDM Advisors
                </span>
              </div>
            )}
            <div className="p-3 flex flex-col gap-1.5">
              {(b.category || [])[0] && (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider self-start px-2 py-0.5 rounded"
                  style={{ background: C.indigo, color: "#fff" }}
                >
                  {b.category[0]}
                </span>
              )}
              <h3
                className="text-[14px] font-bold leading-snug group-hover:underline"
                style={{ color: C.navy }}
              >
                {b.title}
              </h3>
              {b.fields?.metaDescription && (
                <p className="text-[12px] leading-snug line-clamp-2" style={{ color: C.body }}>
                  {b.fields.metaDescription}
                </p>
              )}
              <div className="flex items-center gap-2 text-[11px] mt-1" style={{ color: C.muted }}>
                {b.fields?.author && <span>{b.fields.author}</span>}
                {b.fields?.readingTime && <span>· {b.fields.readingTime} min</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
